import { AuthResult } from '@ionic-enterprise/auth';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { UnlockMode } from '../unlock-mode.type';
import { UserData } from '../../user/interface';
import { VaultService } from '../vault.service';
import { VaultServiceInterface } from '../interfaces';

@Injectable({ providedIn: 'root' })
export class BrowserVaultObfuscatory implements VaultServiceInterface {
  private readonly vaultService: VaultService;

  constructor(vaultService: VaultService) {
    this.vaultService = vaultService;
  }

  public get locked(): Observable<boolean> {
    return this.vaultService.locked;
  }

  public get vaultReady(): Observable<boolean> {
    return this.vaultService.vaultReady;
  }

  public async canUnlock(): Promise<boolean> {
    return this.vaultService.canUnlock();
  }

  public clear(): Promise<void> {
    return this.vaultService.clear();
  }

  public clearSession(): Promise<void> {
    return this.vaultService.clearSession();
  }

  public async getLastUnlockMode(): Promise<UnlockMode> {
    return await this.vaultService.getLastUnlockMode();
  }

  public getSession(): Promise<AuthResult | null> {
    return this.vaultService.getSession();
  }

  public async getUserData(): Promise<UserData | null> {
    return this.vaultService.getUserData();
  }

  public async getValue<T>(key: string): Promise<T | null> {
    return this.vaultService.getValue<T>(key);
  }

  public hasDeviceSecurityEnabled(): Promise<boolean> {
    return this.vaultService.hasDeviceSecurityEnabled();
  }

  public async init(appId: string): Promise<void> {
    await this.vaultService.init(appId);
  }

  public isEmpty(): Promise<boolean> {
    return this.vaultService.isEmpty();
  }

  public isLocked(): Promise<boolean> {
    return this.vaultService.isLocked();
  }

  public async lockVault(): Promise<void> {
    await this.vaultService.lockVault();
  }

  public neverLock(): Promise<boolean> {
    return this.vaultService.neverLock();
  }

  public provision(): Promise<void> {
    return this.vaultService.provision();
  }

  public async removeValue(key: string): Promise<void> {
    await this.vaultService.removeValue(key);
  }

  public saveUserDataFromAuthResult(authResult: AuthResult): Promise<void> {
    return this.vaultService.saveUserDataFromAuthResult(authResult);
  }

  public async setLastUnlockMode(value: UnlockMode): Promise<void> {
    await this.vaultService.setLastUnlockMode(value);
  }

  public setSession(authResult: AuthResult): Promise<void> {
    return this.vaultService.setSession(authResult);
  }

  public async setValue<T>(key: string, value: T): Promise<void> {
    await this.vaultService.setValue<T>(key, value);
  }

  public async unlockVault(): Promise<void> {
    await this.vaultService.unlockVault();
  }

  public toggleLockAfterBackgrounded(enabled: boolean): Promise<void> {
    return this.vaultService.toggleLockAfterBackgrounded(enabled);
  }

  public async updateUnlockMode(mode: UnlockMode): Promise<void> {
    await this.vaultService.updateUnlockMode(mode);
  }
}
